import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const empty = { title: "", date: new Date().toISOString().slice(0, 10), summary: "", body: "", link: "", published: true, order: 0 };

function NewsManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(() => api.get("/admin/news").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const payload = { ...editing, order: Number(editing.order) || 0 };
      if (editing.id) await api.put(`/admin/news/${editing.id}`, payload);
      else await api.post("/admin/news", payload);
      setEditing(null);
      load();
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail)); }
  };
  const del = async (id) => { if (window.confirm("Delete this news item?")) { await api.delete(`/admin/news/${id}`); load(); } };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div><h2>News</h2><p className="hint">Create, edit and publish newsroom articles.</p></div>
        <button className="btn btn-primary" onClick={() => setEditing({ ...empty })} data-testid="news-new-btn">New article</button>
      </div>
      {items.map((n) => (
        <div className="row-item" key={n.id} data-testid={`admin-news-${n.id}`}>
          <div className="row-head">
            <div>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <span className="news-date">{n.date}</span>
                <span className={`pill ${n.published ? "on" : "off"}`}>{n.published ? "Published" : "Draft"}</span>
              </div>
              <h3 style={{ marginTop: 6 }}>{n.title}</h3>
              <p style={{ color: "var(--muted)", fontSize: 14, margin: "6px 0 0" }}>{n.summary}</p>
            </div>
            <div className="row-actions">
              <button className="text-btn" onClick={() => setEditing(n)} data-testid={`news-edit-${n.id}`}>Edit</button>
              <button className="text-btn danger" onClick={() => del(n.id)} data-testid={`news-delete-${n.id}`}>Delete</button>
            </div>
          </div>
        </div>
      ))}
      {editing && (
        <div className="editor-modal" onClick={(e) => e.target === e.currentTarget && setEditing(null)}>
          <form className="editor-box" onSubmit={save} data-testid="news-editor">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3>{editing.id ? "Edit article" : "New article"}</h3>
              <button type="button" className="text-btn" onClick={() => setEditing(null)}>Close</button>
            </div>
            {err && <div className="form-msg err">{err}</div>}
            <div className="field"><label>Title</label><input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} required data-testid="news-f-title" /></div>
            <div className="two-col">
              <div className="field"><label>Date</label><input type="date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} required data-testid="news-f-date" /></div>
              <div className="field"><label>Sort order (higher = top)</label><input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></div>
            </div>
            <div className="field"><label>Summary</label><input value={editing.summary} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} data-testid="news-f-summary" /></div>
            <div className="field"><label>Body</label><textarea value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} data-testid="news-f-body" /></div>
            <div className="field"><label>External link (optional)</label><input value={editing.link} onChange={(e) => setEditing({ ...editing, link: e.target.value })} placeholder="https://" /></div>
            <label style={{ display: "flex", gap: 9, alignItems: "center", fontFamily: "var(--font-d)", fontSize: 14, marginBottom: 20 }}>
              <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="news-f-published" /> Published
            </label>
            <button className="btn btn-primary" type="submit" data-testid="news-save">Save article</button>
          </form>
        </div>
      )}
    </div>
  );
}

function InvestorEditor() {
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState("");
  useEffect(() => { api.get("/investor").then((r) => setD({ documents: [], ...r.data })).catch(() => setD({ intro: "", seeking: "", recruiting: "", documents: [] })); }, []);
  if (!d) return <p className="hint">Loading...</p>;

  const setDoc = (i, k, v) => { const docs = [...d.documents]; docs[i] = { ...docs[i], [k]: v }; setD({ ...d, documents: docs }); };
  const addDoc = () => setD({ ...d, documents: [...d.documents, { label: "", note: "" }] });
  const rmDoc = (i) => setD({ ...d, documents: d.documents.filter((_, x) => x !== i) });

  const save = async () => {
    setMsg("");
    try {
      const { updated_at, ...payload } = d;
      await api.put("/admin/investor", payload);
      setMsg("Saved.");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) { setMsg(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="investor-editor">
      <h2>Investor Relations</h2>
      <p className="hint">Edit the investor content shown on the public page.</p>
      {msg && <div className="form-msg ok">{msg}</div>}
      <div className="card-panel">
        <div className="field"><label>Seeking / opportunity</label><textarea value={d.seeking || ""} onChange={(e) => setD({ ...d, seeking: e.target.value })} data-testid="inv-seeking" /></div>
        <div className="field"><label>Intro / contact note</label><textarea value={d.intro || ""} onChange={(e) => setD({ ...d, intro: e.target.value })} data-testid="inv-intro" /></div>
        <div className="field"><label>Recruiting section</label><textarea value={d.recruiting || ""} onChange={(e) => setD({ ...d, recruiting: e.target.value })} data-testid="inv-recruiting" /></div>
        <label style={{ fontFamily: "var(--font-d)", fontWeight: 600, fontSize: 13.5, display: "block", margin: "6px 0 10px" }}>Investor package documents</label>
        {d.documents.map((doc, i) => (
          <div key={i} style={{ display: "flex", gap: 10, marginBottom: 10 }}>
            <input style={{ flex: "0 0 28%" }} placeholder="Label" value={doc.label} onChange={(e) => setDoc(i, "label", e.target.value)} data-testid={`inv-doc-label-${i}`} />
            <input style={{ flex: 1 }} placeholder="Note" value={doc.note} onChange={(e) => setDoc(i, "note", e.target.value)} />
            <input style={{ flex: "0 0 30%" }} placeholder="Link URL (optional)" value={doc.url || ""} onChange={(e) => setDoc(i, "url", e.target.value)} data-testid={`inv-doc-url-${i}`} />
            <button type="button" className="text-btn danger" onClick={() => rmDoc(i)}>Remove</button>
          </div>
        ))}
        <button type="button" className="btn btn-outline" onClick={addDoc} style={{ marginTop: 6 }}>Add document</button>
        <div style={{ marginTop: 22 }}><button className="btn btn-primary" onClick={save} data-testid="inv-save">Save changes</button></div>
      </div>
    </div>
  );
}

function ContactInbox({ onCount }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/admin/contacts").then((r) => { setItems(r.data); onCount(r.data.filter((c) => !c.read).length); }).catch(() => {}), [onCount]);
  useEffect(() => { load(); }, [load]);

  const view = async (c) => {
    setOpen(c);
    if (!c.read) { await api.patch(`/admin/contacts/${c.id}/read`); load(); }
  };
  const del = async (id) => { if (window.confirm("Delete this message?")) { await api.delete(`/admin/contacts/${id}`); setOpen(null); load(); } };

  return (
    <div data-testid="contact-inbox">
      <h2>Contact inbox</h2>
      <p className="hint">Messages submitted through the public contact form.</p>
      {items.length === 0 && <p style={{ color: "var(--muted)" }}>No messages yet.</p>}
      {items.map((c) => (
        <div className="row-item" key={c.id} data-testid={`inbox-item-${c.id}`} style={{ cursor: "pointer" }} onClick={() => view(c)}>
          <div className="row-head">
            <div>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                {!c.read && <span className="pill unread">New</span>}
                <b style={{ fontFamily: "var(--font-d)" }}>{c.name}</b>
                <span style={{ color: "var(--muted)", fontSize: 13 }}>{c.email}</span>
              </div>
              <h3 style={{ marginTop: 6, fontSize: 16 }}>{c.subject || "(no subject)"}</h3>
              <p style={{ color: "var(--muted)", fontSize: 14, margin: "6px 0 0" }}>{c.message.slice(0, 120)}{c.message.length > 120 ? "..." : ""}</p>
            </div>
            <div className="row-actions">
              <span style={{ color: "var(--muted)", fontSize: 12 }}>{(c.created_at || "").slice(0, 10)}</span>
              <button className="text-btn danger" onClick={(e) => { e.stopPropagation(); del(c.id); }} data-testid={`inbox-delete-${c.id}`}>Delete</button>
            </div>
          </div>
        </div>
      ))}
      {open && (
        <div className="editor-modal" onClick={(e) => e.target === e.currentTarget && setOpen(null)}>
          <div className="editor-box">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3>{open.subject || "(no subject)"}</h3>
              <button className="text-btn" onClick={() => setOpen(null)}>Close</button>
            </div>
            <p style={{ color: "var(--muted)", fontSize: 14 }}>
              From <b>{open.name}</b> · <a href={`mailto:${open.email}`} style={{ color: "var(--brand)" }}>{open.email}</a> · {(open.created_at || "").slice(0, 16).replace("T", " ")}
            </p>
            <div className="msg-body">{open.message}</div>
            <div style={{ marginTop: 20, display: "flex", gap: 10 }}>
              <a className="btn btn-primary" href={`mailto:${open.email}?subject=Re: ${encodeURIComponent(open.subject || "")}`}>Reply by email</a>
              <button className="btn btn-outline" onClick={() => del(open.id)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("news");
  const [unread, setUnread] = useState(0);

  useEffect(() => { api.get("/admin/contacts/unread-count").then((r) => setUnread(r.data.count)).catch(() => {}); }, []);

  return (
    <div className="admin-shell">
      <div className="admin-top">
        <span className="logo-mark" style={{ width: 34, height: 34, fontSize: 13 }}>CD</span>
        <div className="logo-text"><b style={{ fontSize: 15 }}>Cerebral CMS</b></div>
        <div className="admin-tabs">
          <button className={tab === "news" ? "active" : ""} onClick={() => setTab("news")} data-testid="tab-news">News</button>
          <button className={tab === "investor" ? "active" : ""} onClick={() => setTab("investor")} data-testid="tab-investor">Investor</button>
          <button className={tab === "inbox" ? "active" : ""} onClick={() => setTab("inbox")} data-testid="tab-inbox">Inbox {unread > 0 && <span className="admin-badge">{unread}</span>}</button>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 14, alignItems: "center" }}>
          <Link to="/" style={{ color: "#b9cae3", fontSize: 14 }}>View site</Link>
          <span style={{ color: "#8ba0bf", fontSize: 13 }}>{user?.email}</span>
          <button className="text-btn light" onClick={logout} data-testid="logout-btn">Sign out</button>
        </div>
      </div>
      <div className="admin-main">
        {tab === "news" && <NewsManager />}
        {tab === "investor" && <InvestorEditor />}
        {tab === "inbox" && <ContactInbox onCount={setUnread} />}
      </div>
    </div>
  );
}
